import React from 'react'

import useOpenJumpInfo from '../lib/use-open-jump-info'

/**
 * Component for displaying Open Jump hours in the page header tables
 * Shows special holiday schedule message or regular weekly hours tables
 */
export const OpenJumpHeaderTables = () => {
  const { cgHours, dfHours } = useOpenJumpInfo()

  // If special hours are active, show the holiday schedule
  if (cgHours.type === 'special') {
    return (
      <div className="mt-8 w-full">
        <div className="text-center mb-6 p-4 bg-white rounded-lg mx-auto max-w-3xl shadow-md">
          <p className="text-2xl md:text-3xl font-bold text-display text-grey-darkest">
            🎄 Holiday Hours 🎁
            <br />
            (Dec 23 - Jan 3)
          </p>
        </div>

        <div className="mx-auto max-w-3xl bg-white rounded-lg shadow-lg overflow-hidden border-4 border-white">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="text-center pt-4 pb-3 px-2 md:px-4 font-bold text-white text-base md:text-lg text-display text-shadow bg-grey-darkest rounded-tl leading-none">
                  Date
                </th>
                <th className="text-center pt-4 pb-3 px-2 md:px-4 font-bold text-white text-base md:text-lg text-display text-shadow bg-gradient-orange leading-none">
                  Cottage Grove
                </th>
                <th className="text-center pt-4 pb-3 px-2 md:px-4 font-bold text-white text-base md:text-lg text-display text-shadow bg-gradient-purple rounded-tr leading-none">
                  DeForest
                </th>
              </tr>
            </thead>
            <tbody>
              {cgHours.data.map((cgEntry, idx) => {
                const dfEntry = dfHours.data[idx]
                return (
                  <tr key={idx} className="border-b border-grey-light">
                    <td className="py-2 md:py-3 px-2 md:px-4 font-semibold text-grey-darkest text-sm md:text-base">
                      {cgEntry.date}
                    </td>
                    <td
                      className={`py-2 md:py-3 px-2 md:px-4 text-sm md:text-base ${
                        cgEntry.times === 'Closed'
                          ? 'text-grey-dark font-medium'
                          : 'text-grey-darkest'
                      }`}
                    >
                      {cgEntry.times}
                    </td>
                    <td
                      className={`py-2 md:py-3 px-2 md:px-4 text-sm md:text-base ${
                        dfEntry.times === 'Closed'
                          ? 'text-grey-dark font-medium'
                          : 'text-grey-darkest'
                      }`}
                    >
                      {dfEntry.times}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  // Regular hours - render existing table structure using config data
  const days = ['Tuesday', 'Thursday', 'Friday', 'Saturday']
  const dayAbbrev = {
    Tuesday: 'Tues',
    Thursday: 'Thurs',
    Friday: 'Fri',
    Saturday: 'Sat',
  }

  return (
    <>
      {/* Mobile table - shows days as rows, locations as columns */}
      <table className="xl:hidden table-fixed w-full md:w-4/5 mx-auto mt-8">
        <tbody>
          <tr className="text-2xl text-display uppercase">
            <td></td>
            <td>{cgHours.data.name}</td>
            <td>{dfHours.data.name}</td>
          </tr>
          {days.map(day => (
            <tr key={day}>
              <td className="py-2 text-2xl text-display uppercase">
                {dayAbbrev[day]}
              </td>
              <td>{cgHours.data.byDay[day]}</td>
              <td>{dfHours.data.byDay[day]}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Desktop table - shows locations as rows, days as columns */}
      <table className="hidden xl:table table-fixed w-full mt-8">
        <tbody>
          <tr className="text-2xl text-display uppercase">
            <td></td>
            {days.map(day => (
              <td key={day}>{dayAbbrev[day]}</td>
            ))}
          </tr>
          <tr>
            <td className="py-2 text-2xl text-display uppercase">
              {cgHours.data.name}
            </td>
            {days.map(day => (
              <td key={day}>{cgHours.data.byDay[day]}</td>
            ))}
          </tr>
          <tr>
            <td className="py-2 text-2xl text-display uppercase">
              {dfHours.data.name}
            </td>
            {days.map(day => (
              <td key={day}>{dfHours.data.byDay[day]}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </>
  )
}

/**
 * Component for displaying Open Jump hours in the offering card footer
 * Shows special holiday notice or regular weekly hours for both locations
 */
export const OpenJumpCardHours = () => {
  const { cgHours, dfHours } = useOpenJumpInfo()

  // If special hours are active, show condensed holiday schedule
  if (cgHours.type === 'special') {
    return (
      <div className="px-3 py-3 border border-grey-light rounded">
        <div className="text-center mb-3 pb-2 border-b-2 border-orange">
          <p className="font-bold text-grey-darkest text-xs">
            🎄 Holiday Hours (Dec 23 - Jan 3)
          </p>
        </div>

        <table className="w-full border-collapse text-xs">
          <thead>
            <tr>
              <th className="text-center pt-3 pb-2 px-2 font-bold text-white text-sm text-display text-shadow bg-grey-darkest rounded-tl leading-none">
                Date
              </th>
              <th className="text-center pt-3 pb-2 px-2 font-semibold text-white text-sm tracking-wide text-display text-shadow bg-gradient-orange leading-none">
                Cottage Grove
              </th>
              <th className="text-center pt-3 pb-2 px-2 font-semibold text-white text-sm tracking-wide text-display text-shadow bg-gradient-purple rounded-tr leading-none">
                DeForest
              </th>
            </tr>
          </thead>
          <tbody>
            {cgHours.data.map((cgEntry, idx) => {
              const dfEntry = dfHours.data[idx]
              return (
                <tr key={idx} className="border-b border-grey-lighter">
                  <td className="py-1 px-1 font-semibold text-grey-darkest">
                    {cgEntry.date}
                  </td>
                  <td
                    className={`py-1 px-1 ${
                      cgEntry.times === 'Closed'
                        ? 'text-grey-dark font-medium'
                        : 'text-grey-darkest'
                    }`}
                  >
                    {cgEntry.times === 'Closed' ? 'Closed' : cgEntry.times}
                  </td>
                  <td
                    className={`py-1 px-1 ${
                      dfEntry.times === 'Closed'
                        ? 'text-grey-dark font-medium'
                        : 'text-grey-darkest'
                    }`}
                  >
                    {dfEntry.times === 'Closed' ? 'Closed' : dfEntry.times}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  }

  // Regular hours display as table
  const days = ['Tuesday', 'Thursday', 'Friday', 'Saturday']
  const dayAbbrev = {
    Tuesday: 'Tues',
    Thursday: 'Thurs',
    Friday: 'Fri',
    Saturday: 'Sat',
  }

  return (
    <div className="px-3 py-3 border border-grey-light rounded">
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="text-center pt-3 pb-2 px-2 font-bold text-white text-sm text-display text-shadow bg-grey-darkest rounded-tl leading-none">
              Day
            </th>
            <th className="text-center pt-3 pb-2 px-2 font-semibold text-white text-sm text-display text-shadow bg-gradient-orange leading-none">
              {cgHours.data.name}
            </th>
            <th className="text-center pt-3 pb-2 px-2 font-semibold text-white text-sm text-display text-shadow bg-gradient-purple rounded-tr leading-none">
              {dfHours.data.name}
            </th>
          </tr>
        </thead>
        <tbody>
          {days.map(day => (
            <tr key={day} className="border-b border-grey-lighter">
              <td className="py-1 px-1 font-semibold text-grey-darkest">
                {dayAbbrev[day]}
              </td>
              <td className="py-1 px-1 text-grey-darkest">
                {cgHours.data.byDay[day]}
              </td>
              <td className="py-1 px-1 text-grey-darkest">
                {dfHours.data.byDay[day]}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
